import * as React from "react";
import type { MemoryGraphNode, MemoryEntry } from "@zworks/console-contracts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, FileText, X } from "lucide-react";

interface MemoryNodeInspectorProps {
  node: MemoryGraphNode | null;
  entries: MemoryEntry[];
  onClose: () => void;
}

export function MemoryNodeInspector({ node, entries, onClose }: MemoryNodeInspectorProps) {
  if (!node) return null;

  const isConcept = node.type === "concept";

  // Filter linked entries matching this concept/entity
  const linkedEntries = isConcept
    ? entries.filter(
        (e) =>
          node.id === "concept-project-core" ||
          e.body.toLowerCase().includes(node.name.toLowerCase().split(" ")[0] || "") ||
          (e.ref && e.ref.toLowerCase().includes(node.name.toLowerCase().split(" ")[0] || "")),
      )
    : entries.filter((e) => e.body === node.description || (e.ref && node.source === e.ref));

  return (
    <aside
      aria-label="Memory Node Inspector"
      className="flex h-full flex-col border-l border-border/70 bg-card text-foreground"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border/70 p-4">
        <div className="flex min-w-0 items-center gap-2.5">
          {isConcept ? (
            <div className="grid size-8 shrink-0 place-items-center rounded-lg border border-lime/30 bg-lime/10 text-chart dark:text-lime">
              <Sparkles className="size-4" aria-hidden />
            </div>
          ) : (
            <div className="grid size-8 shrink-0 place-items-center rounded-lg border border-border/60 bg-muted/40 text-foreground">
              <FileText className="size-4" aria-hidden />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-sm text-foreground">{node.name}</h3>
            <span className="font-mono text-xs text-muted-foreground">{node.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isConcept ? "lime" : "secondary"} className="uppercase font-mono text-xs">
            {node.type}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 rounded-md text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close inspector"
          >
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Description / Summary */}
        <div className="rounded-lg border border-border/60 bg-muted/20 p-3 text-xs leading-relaxed">
          <p className="font-medium text-foreground">
            {node.description || "No description provided."}
          </p>
        </div>

        {/* Node Properties */}
        <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Level / Tier:</span>
            <span className="font-medium">
              {isConcept ? "High-level Concept" : "Low-level Entity"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Provenance Source:</span>
            <span className="font-mono text-xs truncate max-w-[180px]">
              {node.source || "Project Scope"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Related Facts:</span>
            <span className="font-mono font-semibold">{node.factCount}</span>
          </div>
        </div>

        {/* Linked Knowledge Facts List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Linked Memory Facts ({linkedEntries.length})</span>
          </div>

          {linkedEntries.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No direct fact bodies matched.</p>
          ) : (
            <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
              {linkedEntries.map((fact) => (
                <div
                  key={fact.id}
                  className="rounded-lg border border-border/60 bg-muted/30 p-2.5 text-xs space-y-1.5 transition-colors hover:border-chart/40"
                >
                  <p className="font-mono text-xs leading-normal text-foreground whitespace-pre-wrap">
                    {fact.body}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/40">
                    <span className="font-mono truncate max-w-[140px]">
                      {fact.ref ?? "project-memory"}
                    </span>
                    <span>Used by {fact.runsUsed} runs</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
